import { Controller } from '@hotwired/stimulus'
import { i18n } from 'config/translations'
import { programEndDate } from 'utils/plan_duration_calculator'

export default class extends Controller {
  static targets = [
    'summaryText',
    'planPrice',
    'adultUnitPrice',
    'childUnitPrice',
    'elementarySchoolStudentUnitPrice',
    'childStayOnlyUnitPrice',
    'elementarySchoolStudentStayOnlyUnitPrice',
  ]
  static values = {
    startDate: String,
    stayDays: String,
    programDays: String,
    // [hrg-ui2] 宿なしプラン（部屋なし / School Fee Only）判定。true のとき滞在帯の終了日を
    // 宿泊数ではなく留学日数(program_days)の平日カウントで求める（バックエンド roomless_plan? と同一基準）。
    roomless: Boolean,
    enabledDates: String,
    month: String,
    planId: String,
    // [FB-64] 日付未選択時に内訳モーダルを初期表示するための最低価格(@minimum_price)。
    planPrice: Number,
    adultStayFee: Number,
    preschoolProgramFee: Number,
    preschoolStayFee: Number,
    primaryProgramFee: Number,
    primaryStayFee: Number,
  }

  // [hrg-ui2] チェックイン日から滞在帯（ハイライト範囲）の終了日を求める。
  // - 宿なしプラン: 留学日数(program_days)を平日カウントした終了日（バックエンド end_date_for_summary と同一規則）。
  //   宿泊数(stay_day)=0 のため従来の「選択日 + 宿泊数」では帯が出ず、留学期間がハイライトされなかった。
  // - 部屋ありプラン: 従来通り 選択日 + 宿泊数。
  endDateFor(startDate) {
    if (this.roomlessValue) {
      return programEndDate(startDate, this.programDaysValue)
    }
    const endDate = new Date(startDate)
    endDate.setDate(startDate.getDate() + parseInt(this.stayDaysValue))
    return endDate
  }

  initialPrice(planPrice, adultStayFee, preschoolProgramFee, preschoolStayFee, primaryProgramFee, primaryStayFee) {
    const stayNights = parseInt(this.stayDaysValue) || 0
    const programDays = parseInt(this.programDaysValue) || stayNights
    // 1人あたり合計 = program_fee × program_days + stay_fee × stay_nights
    const adultTotal = (adultStayFee || 0) * stayNights
    const preschoolTotal = (preschoolProgramFee || 0) * programDays + (preschoolStayFee || 0) * stayNights
    const primaryTotal = (primaryProgramFee || 0) * programDays + (primaryStayFee || 0) * stayNights
    const preschoolStayTotal = (preschoolStayFee || 0) * stayNights
    const primaryStayTotal = (primaryStayFee || 0) * stayNights
    if (i18n.locale === 'ja') {
      this.planPriceTarget.textContent = i18n.t('price_text', { price: planPrice.toLocaleString('en-US') })
      this.adultUnitPriceTarget.textContent = i18n.t('price_text', {
        price: adultTotal.toLocaleString('en-US'),
      })
    }
    if (i18n.locale === 'ja') {
      this.childUnitPriceTarget.textContent = i18n.t('plan_detail.reservation.modal.breakdown.nursery_aboard', {
        unit_price: preschoolTotal.toLocaleString('en-US'),
      })
    } else {
      this.childUnitPriceTarget.textContent = i18n.t('plan_detail.reservation.modal.breakdown.nursery_aboard')
    }
    if (i18n.locale === 'ja') {
      this.elementarySchoolStudentUnitPriceTarget.textContent = i18n.t(
        'plan_detail.reservation.modal.breakdown.elementary_aboard',
        {
          unit_price: primaryTotal.toLocaleString('en-US'),
        },
      )
    } else {
      this.elementarySchoolStudentUnitPriceTarget.textContent = i18n.t(
        'plan_detail.reservation.modal.breakdown.elementary_aboard',
      )
    }
    if (i18n.locale === 'ja') {
      this.childStayOnlyUnitPriceTarget.textContent = i18n.t('plan_detail.reservation.modal.breakdown.not_and_toddler', {
        unit_price: preschoolStayTotal.toLocaleString('en-US'),
      })
    } else {
      this.childStayOnlyUnitPriceTarget.textContent = i18n.t('plan_detail.reservation.modal.breakdown.not_and_toddler')
    }
    if (i18n.locale === 'ja') {
      this.elementarySchoolStudentStayOnlyUnitPriceTarget.textContent = i18n.t(
        'plan_detail.reservation.modal.breakdown.not_and_elementary',
        {
          unit_price: primaryStayTotal.toLocaleString('en-US'),
        },
      )
    } else {
      this.elementarySchoolStudentStayOnlyUnitPriceTarget.textContent = i18n.t(
        'plan_detail.reservation.modal.breakdown.not_and_elementary',
      )
    }
  }

  connect() {
    setTimeout(function () {
      document.getElementById('order-number').classList.remove('hidden')
    }, 500)
    // 選べる日が無いときは日付の欄がこの controller の target にならない（date_range_selection.haml）
    if (!this.hasSummaryTextTarget) return

    this.summaryTextTarget.textContent = i18n.t('plan_detail.reservation.date_range.select')
    // [#2416] 選べない日の ?start_date= は、選ばれていない扱いにする（予約ボタンまで値が届かないように）
    if (this.startDateValue && !this.enabledDatesValue.split(',').includes(this.startDateValue)) {
      this.startDateValue = ''
    }
    if (this.startDateValue) {
      // 初めから選んである日は、モバイルでも日付の欄と料金を出す（#2416 より前はモバイルだけ出ていなかった）
      this.save()
    } else if (this.planPriceValue > 0) {
      // [FB-64] 日付未選択（初回アクセス）でも内訳モーダルに金額・内訳ラベルを表示する。
      // 最低価格(@minimum_price)で内訳を初期化しておき、日付を選ぶと save() が選択日の価格で上書きする。
      this.initialPrice(
        this.planPriceValue,
        this.adultStayFeeValue,
        this.preschoolProgramFeeValue,
        this.preschoolStayFeeValue,
        this.primaryProgramFeeValue,
        this.primaryStayFeeValue,
      )
    }
    if (this.monthValue) {
      this.openAtMonth()
    }
  }

  // ?month=N（今月から N か月先）を付けて開いたときは、カレンダーを開いてその月までスクロールする
  // shortcut: Turbo の遷移で開いたときはカレンダーがまだマウントされておらずスクロールしない。サイト内に ?month= のリンクを足すならカレンダーの側でスクロールする
  openAtMonth() {
    const now = new Date()
    const target = new Date(now.getFullYear(), now.getMonth() + parseInt(this.monthValue), 1)
    const month = `${target.getFullYear()}-${String(target.getMonth() + 1).padStart(2, '0')}`
    const open = () => {
      document.getElementById('cal-button').click()
      // カレンダー（client/components/plan-calendar）は開いて見えるようになってから、次の描画でスクロールできる
      requestAnimationFrame(() => this.element.querySelector(`[data-calendar-month="${month}"]`)?.scrollIntoView())
    }
    // Turbo 遷移や、読み込みが済んでから connect したときは load がもう来ないので、すぐ開く
    if (document.readyState === 'complete') {
      open()
    } else {
      window.addEventListener('load', open, { once: true })
    }
  }

  // カレンダー（client/components/plan-calendar）で日付を選んだとき（plan-calendar:select）。
  // PC はすぐ日付の欄と料金に反映し、モバイルは保存ボタン（save）で反映する
  selected(event) {
    this.startDateValue = event.detail.date
    if (!this.mobileCheck()) {
      this.save()
    }
  }

  save() {
    if (!this.startDateValue) {
      return
    }
    // 'YYYY-MM-DD' を new Date() に渡すと UTC として読まれ、UTC より西のタイムゾーンでは前の日になる
    const [year, month, day] = this.startDateValue.split('-').map(Number)
    const selectedDate = new Date(year, month - 1, day)
    const endDate = this.endDateFor(selectedDate)
    const dateString = `${month}/${day}-${endDate.getMonth() + 1}/${endDate.getDate()}`

    const totalPriceController = this.application.getControllerForElementAndIdentifier(this.element, 'total-price')
    // connect の順は controller の読み込み方で変わる（importmap の動的 import は読み込みの済んだ順）。
    // total-price がまだ居ないときは取りにいけないので、total-price の connect から取り直してもらう（retryPendingSave）
    this.savePending = !totalPriceController
    if (totalPriceController) {
      const url = `/plans/${this.planIdValue}/price_details?start_date=${this.startDateValue}`

      fetch(url, { headers: { Accept: 'application/json' } })
        .then((response) => {
          // fetch は axios と違い 4xx/5xx でも reject しない。ここで明示的にエラーへ倒さないと
          // [FB-64] の 404 / 422 が下の catch に届かず、料金内訳が無言の 0 円表示になる。
          if (!response.ok) {
            throw new Error(`price_details responded ${response.status}`)
          }
          return response.json()
        })
        .then((priceDetails) => {
          const planPrice = parseInt(priceDetails.plan_price) || 0
          const adultStayFee = parseInt(priceDetails.adult_stay_fee) || 0
          const preschoolProgramFee = parseInt(priceDetails.preschool_program_fee) || 0
          const preschoolStayFee = parseInt(priceDetails.preschool_stay_fee) || 0
          const primaryProgramFee = parseInt(priceDetails.primary_program_fee) || 0
          const primaryStayFee = parseInt(priceDetails.primary_stay_fee) || 0
          const stayNights = parseInt(this.stayDaysValue) || 0
          const programDays = parseInt(priceDetails.program_days) || stayNights
          // 1人あたり合計 = program_fee × program_days + stay_fee × stay_nights
          const adultTotal = adultStayFee * stayNights
          const preschoolTotal = preschoolProgramFee * programDays + preschoolStayFee * stayNights
          const primaryTotal = primaryProgramFee * programDays + primaryStayFee * stayNights
          const preschoolStayTotal = preschoolStayFee * stayNights
          const primaryStayTotal = primaryStayFee * stayNights
          totalPriceController.setPrice(
            planPrice,
            adultStayFee,
            preschoolProgramFee,
            preschoolStayFee,
            primaryProgramFee,
            primaryStayFee,
          )
          totalPriceController.setProgramDays(programDays)
          totalPriceController.calculate()
          this.summaryTextTarget.textContent = dateString
          if (i18n.locale === 'ja') {
            this.planPriceTarget.textContent = i18n.t('price_text', { price: planPrice.toLocaleString('en-US') })
            this.adultUnitPriceTarget.textContent = i18n.t('price_text', {
              price: adultTotal.toLocaleString('en-US'),
            })
          }
          if (i18n.locale === 'ja') {
            this.childUnitPriceTarget.textContent = i18n.t('plan_detail.reservation.modal.breakdown.nursery_aboard', {
              unit_price: preschoolTotal.toLocaleString('en-US'),
            })
          } else {
            this.childUnitPriceTarget.textContent = i18n.t('plan_detail.reservation.modal.breakdown.nursery_aboard')
          }
          if (i18n.locale === 'ja') {
            this.elementarySchoolStudentUnitPriceTarget.textContent = i18n.t(
              'plan_detail.reservation.modal.breakdown.elementary_aboard',
              {
                unit_price: primaryTotal.toLocaleString('en-US'),
              },
            )
          } else {
            this.elementarySchoolStudentUnitPriceTarget.textContent = i18n.t(
              'plan_detail.reservation.modal.breakdown.elementary_aboard',
            )
          }
          if (i18n.locale === 'ja') {
            this.childStayOnlyUnitPriceTarget.textContent = i18n.t('plan_detail.reservation.modal.breakdown.not_and_toddler', {
              unit_price: preschoolStayTotal.toLocaleString('en-US'),
            })
          } else {
            this.childStayOnlyUnitPriceTarget.textContent = i18n.t('plan_detail.reservation.modal.breakdown.not_and_toddler')
          }
          if (i18n.locale === 'ja') {
            this.elementarySchoolStudentStayOnlyUnitPriceTarget.textContent = i18n.t(
              'plan_detail.reservation.modal.breakdown.not_and_elementary',
              {
                unit_price: primaryStayTotal.toLocaleString('en-US'),
              },
            )
          } else {
            this.elementarySchoolStudentStayOnlyUnitPriceTarget.textContent = i18n.t(
              'plan_detail.reservation.modal.breakdown.not_and_elementary',
            )
          }
          return null
        })
        .catch((error) => {
          // [FB-64] 取得失敗（料金未連携時の 422 / 予期しない 5xx 等）に無言の空欄のままにせず、エラーを明示する。
          console.error('price_details fetch failed', error)
          this.summaryTextTarget.textContent = i18n.t('message.error.code.e_000')
          return null
        })
    }
  }

  // total-price の connect から呼ばれる。total-price が居なくて save() が料金を取りにいけなかったときだけ取り直す
  retryPendingSave() {
    if (this.savePending) this.save()
  }

  getSelectedDate() {
    if (this.startDateValue) {
      return this.startDateValue
    }
  }

  mobileCheck = function () {
    let check = false
    ;(function (a) {
      if (
        /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
          a,
        ) ||
        /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(
          a.substr(0, 4),
        )
      ) {
        check = true
      }
    })(navigator.userAgent || navigator.vendor || window.opera)
    return check
  }

  clear() {
    this.summaryTextTarget.textContent = i18n.t('plan_detail.reservation.date_range.select')
    this.startDateValue = ''
    this.element.querySelector('#plan-calendar')?.dispatchEvent(new CustomEvent('plan-calendar:clear'))
  }
}
